/**
 * Kindred Open Data partner APIs (merchant-map, product-map, promo-discovery)
 * surfaced read-only for QA validation. Field names mirror the upstream
 * payloads so a reviewer comparing raw JSON to the UI sees the same keys.
 */

/** Traffic-light state for one validation row. */
export enum OpenDataCheckStatus {
  Pass = "pass",
  Warn = "warn",
  Fail = "fail",
  Error = "error",
}

/** Every Open Data endpoint wraps its payload in this envelope. */
export interface IOpenDataEnvelope<T> {
  isSuccessful: boolean;
  message: string;
  data: T | null;
  statusCode: number;
}

/** Upstream only distinguishes Mobile from Desktop; anything else falls back to Mobile. */
export enum OpenDataViewType {
  Mobile = "Mobile",
  Desktop = "Desktop",
}

// ── Merchant map ────────────────────────────────────────

export interface IOpenDataMerchantMapProduct {
  productUrlPattern: string;
  productSizeSelector: string;
  addToCartSelector: string;
  variants: unknown[];
  productDetailsToggleSelector: string;
  productDetailsAdditionalToggleSelector: string;
}

export interface IOpenDataMerchantMapCart {
  cartUrlPattern: string;
  itemNameSelector: string;
  itemPriceSelector: string;
  itemOriginalPriceSelector: string;
  itemProductIdSelector: string;
  itemProductUrlSelector: string;
  itemCategorySelector: string;
  itemQuantitySelector: string;
  basketAmountSelector: string;
  cartCheckoutSelector: string;
  emptyCartSelector: string;
  loadingSelector: string;
}

export interface IOpenDataMerchantMapShipping {
  shippingNameSelector: string;
  shippingAddressSelector: string;
  shippingEmailSelector: string;
}

export interface IOpenDataMerchantMapCheckout {
  checkoutUrl: string;
  checkoutPageUrlRegex: string;
  checkoutUrlPattern: string;
  toggleCouponBoxSelector: string;
  extraToggleCouponBoxSelector: string;
  couponBoxSelector: string;
  submitCodeButtonSelector: string;
  failureAlertSelector: string;
  successAlertSelector: string;
  removeCodeSelector: string;
  confirmRemoveCodeSelector: string;
  nonRemovablePreAppliedCodeSelector: string;
  preAppliedCodeSelector: string;
  preAppliedSavingsSelector: string;
  deliveryAmountSelector: string;
  applyCodeTimeout: string;
  removeCodeTimeout: string;
  continueButtonSelector: string;
  taxAmountSelector: string;
  shippingProtectionAmountSelector: string;
  dutiesAmountSelector: string;
  bagFeeAmountSelector: string;
  miscellaneousFeeAmountSelector: string;
  discountAmountSelector: string;
  finalPriceSelector: string;
  pageLoadingSelector: string;
  failureCheckFrequency: number;
  couponBoxCheckFrequency: number;
  betweenActionTimeout: number;
  betweenCodesTimeout: number;
  hasSiteRefresh: boolean;
  manualRefresh: boolean;
}

export interface IOpenDataMerchantMap {
  id: string;
  domain: string;
  viewType: string;
  version: string;
  product: IOpenDataMerchantMapProduct;
  cart: IOpenDataMerchantMapCart;
  shipping: IOpenDataMerchantMapShipping;
  checkout: IOpenDataMerchantMapCheckout;
}

/** Per-section fill counts, so a non-technical reviewer can read coverage at a glance. */
export interface IOpenDataMerchantMapSection {
  key: string;
  label: string;
  filled: number;
  total: number;
  /** Fields carrying a non-empty value, in upstream order. */
  populatedFields: string[];
  emptyFields: string[];
}

/**
 * What a map can actually drive. Judged on named selectors only — the checkout
 * block always ships numeric tuning defaults, so counting any non-empty field
 * would mark every merchant as having a working auto-coupon template.
 */
export interface IOpenDataMerchantMapCapabilities {
  /** Product page mapping: product URL pattern or add-to-cart. */
  product: boolean;
  /** Auto-coupon template: a coupon box and a submit button. */
  autoTemplate: boolean;
  /** SKU/basket mapping: cart item or basket total selectors. */
  skuTemplate: boolean;
  /** Shipping form mapping. */
  shipping: boolean;
}

export interface IOpenDataMerchantMapResult {
  domain: string;
  viewType: string;
  found: boolean;
  /** Upstream `message` when the map is missing (e.g. "Merchant map not found"). */
  message: string;
  map: IOpenDataMerchantMap | null;
  sections: IOpenDataMerchantMapSection[];
  capabilities: IOpenDataMerchantMapCapabilities;
  /**
   * Red without product or auto template, amber with both, green once a SKU
   * or shipping mapping is there too.
   */
  status: OpenDataCheckStatus;
  /** Why the map landed on that status, in one line. */
  statusReason: string;
}

// ── Product map ─────────────────────────────────────────

export interface IOpenDataProduct {
  id: string;
  name: string;
  categories: string[];
  merchantName: string | null;
  domain: string | null;
  kindredMerchantId: string | null;
  productUrl: string | null;
  merchantUrl: string | null;
  imageUrl: string | null;
  manufacturer: string | null;
  manufacturerCanonical: string | null;
  sku: string | null;
  ean: string | null;
  mpn: string | null;
  price: number | null;
  originalPrice: number | null;
  salePrice: number | null;
  currency: string | null;
  inStock: boolean | null;
}

export interface IOpenDataProductMatch {
  product: IOpenDataProduct;
  matchConfidence: number;
  matchType: string;
  matchedKey: string;
  matchReason: string;
  applicableOffers: unknown[];
  /** Derived from the `<Network>_<advertiser>_<sku>` id prefix; upstream does not send it. */
  network: string | null;
}

/**
 * Confidence is a grid of "how many words matched" by "was the merchant domain
 * pinned", so the same search scores lower without a domain:
 *
 *   words matched   no domain   + domain
 *   1               0.4         0.55
 *   2               0.5         0.65
 *   3+              0.6         0.75
 *   manufacturer key    —       0.98
 *   exact SKU/EAN/MPN            1.0
 *
 * Thresholds are therefore set at the no-domain values, so a tier means the
 * same thing either way. The upstream filter is inclusive.
 */
export enum OpenDataConfidence {
  /** Everything, including single-word and category-only hits. */
  Any = 0,
  /** Two or more words, or a single word on the same domain. */
  Balanced = 0.5,
  /** Three or more words, or two words on the same domain. */
  Strong = 0.6,
  /** Three or more words, on the same domain. */
  VeryStrong = 0.75,
  /** Scoped manufacturer key, or an exact SKU/EAN/MPN. */
  Exact = 0.98,
}

/** Drops the single-word noise tier while still working without a domain. */
export const PRODUCT_MAP_DEFAULT_CONFIDENCE = OpenDataConfidence.Balanced;

/**
 * At least one of productName/sku/productUrl/manufacturer/category is required
 * upstream; a request with only `domain` is rejected with a 400.
 */
export interface IOpenDataProductMapQuery {
  productName?: string;
  sku?: string;
  productUrl?: string;
  manufacturer?: string;
  category?: string;
  domain?: string;
  /** ISO country code, e.g. "UK". Narrows matches to that market's feeds. */
  countryCode?: string;
  /** Two-letter lowercase language code, e.g. "fr". Narrows matches to that language's feeds. */
  language?: string;
  /** Upstream caps this at 50. `skipCount` is accepted but ignored, so paging is client-side. */
  maxResultCount?: number;
  /** Inclusive floor on `matchConfidence`; 0 returns everything. */
  minimumConfidence?: number;
  /**
   * Restrict matches to items the feed currently reports as in stock.
   *
   * `false` (the default) returns everything the matcher finds, in stock or
   * not. `true` returns only what can actually be bought right now.
   *
   * Left defaulted to `false` on purpose: a validation run is asking whether a
   * domain has usable product DATA, and a catalogue that happens to be out of
   * stock today is still good data. Turning it on answers a different question
   * — what a shopper could buy — which is the one worth asking before pointing
   * a test at a product.
   */
  inStockOnly?: boolean;
}

export interface IOpenDataProductMapResult {
  count: number;
  results: IOpenDataProductMatch[];
  /** The threshold actually applied, echoed so the UI can state it. */
  minimumConfidence: number;
  /**
   * When the threshold filtered everything out, the best score the same search
   * scores with no floor — so the UI can name the tier to drop to. Null when
   * there were results, or nothing matches at any threshold.
   */
  bestAvailableConfidence: number | null;
  /** Product counts per derived network, for the result summary strip. */
  networkCounts: Record<string, number>;
  /** True when `count` hit the upstream ceiling, so more may exist. */
  truncated: boolean;
}

// ── Promo discovery ─────────────────────────────────────

export interface IOpenDataPromoDiscount {
  discountValue: number | null;
  maxDiscountValue: number | null;
  minimumSpend: number | null;
}

export interface IOpenDataPromoMetadata {
  currency: string | null;
  language: string | null;
  discountType: number | null;
  promoType: number | null;
  nonMonetaryValue: string | null;
  /** Matches KindredCouponCoverage: 0 none, 1 site-wide, 2 category, 4 product. */
  coverage: number | null;
  scope: number | null;
  category: string | null;
  discounts: IOpenDataPromoDiscount[] | null;
}

export interface IOpenDataPromo {
  promoId: string;
  brandName: string | null;
  brandId: string | null;
  domain: string | null;
  promoType: string | null;
  logoUrl: string | null;
  code: string | null;
  description: string | null;
  promoLink: string | null;
  activationLink: string | null;
  priority: number | null;
  startDate: string | null;
  endDate: string | null;
  metadata: IOpenDataPromoMetadata | null;
}

/** A deal inferred from a promo's activation link. */
export interface IOpenDataDeal {
  dealId: string;
  partnerId: string | null;
  /** How many promos activate through this deal. */
  promoCount: number;
}

export interface IOpenDataPromoResult {
  domain: string;
  /** Upstream calls this `countryCode`; `location` is silently ignored. */
  countryCode: string | null;
  /** Two-letter lowercase language code, e.g. "fr". */
  language: string | null;
  includeOffers: boolean;
  /** The brand-level deal link, alongside each promo's own activation link. */
  dealLink: string | null;
  promos: IOpenDataPromo[];
  total: number;
  /** Promos with a non-empty `code`. */
  withCode: number;
  /** Promos carrying a `metadata` block. */
  withMetadata: number;
  /** Promos whose metadata names a coverage value. */
  withCoverage: number;
  /** Counts keyed by promoType ("Coupon", "Deal", …). */
  promoTypeCounts: Record<string, number>;
  /** Counts keyed by coverage label ("Site-Wide", "Category", "Product", "Not Set"). */
  coverageCounts: Record<string, number>;
  /** Promos with a usable activation link. */
  withActivationLink: number;
  /** Distinct deals behind those activation links. */
  deals: IOpenDataDeal[];
}

// ── Domain/geo validation ───────────────────────────────

/** Whether a result came from the Kindred partner APIs or one of our own services. */
export enum OpenDataSourceKind {
  Partner = "partner",
  Internal = "internal",
}

/** Names the call behind a result, so a reviewer can reproduce it. */
export interface IOpenDataSourceRef {
  kind: OpenDataSourceKind;
  /** Short name shown on the card, e.g. "promo-discovery". */
  name: string;
  /** Path of the upstream call. */
  endpoint: string;
}

export interface IOpenDataCheck {
  key: string;
  label: string;
  status: OpenDataCheckStatus;
  /** One-line verdict written for a non-technical reviewer. */
  summary: string;
  /** Supporting counts rendered as a small key/value strip. */
  details: Record<string, string | number>;
  /** Set when the upstream call itself failed, rather than returning "nothing found". */
  error?: string;
  /** Which API produced this row. */
  source: IOpenDataSourceRef;
}

export interface IOpenDataFeedAdvertiser {
  network: string;
  advertiserId: string;
  name: string;
  domain: string | null;
  productCount: number;
  /** Used to seed the product sample, which has no domain-only query. */
  categories: string[];
  region: string | null;
  lastDiscoveredAt: string | null;
}

export interface IOpenDataValidationResult {
  domain: string;
  country: string;
  /** Two-letter lowercase language code passed to promo discovery; null when unset. */
  language: string | null;
  /** Product keyword the run searched on; null when none was given. */
  keyword: string | null;
  checkedAt: string;
  checks: IOpenDataCheck[];
  promos: IOpenDataPromoResult | null;
  /** Null unless a keyword was given — product map cannot be queried by domain alone. */
  productMap: IOpenDataProductMapResult | null;
  merchantMaps: IOpenDataMerchantMapResult[];
  feed: {
    advertisers: IOpenDataFeedAdvertiser[];
    totalProducts: number;
    networks: string[];
    source: IOpenDataSourceRef;
  } | null;
}
